import { useEffect, useRef, type RefObject } from "react";

/** How close to the bottom (px) still counts as "reading the latest". */
const NEAR_BOTTOM = 80;

/**
 * Keeps a scrolling panel on its newest content while the reader is already there: new steps, the
 * answer and the proposal card appear below, so without this they arrive out of sight. A reader who
 * has scrolled up to read something older is left where they are, except when a new question starts.
 *
 * @param version changes whenever the content grows
 * @param questionStarted true on the render a new question begins, which always follows
 */
export function useFollowLatest(panel: RefObject<HTMLElement | null>, version: unknown, questionStarted: boolean): void {
  const atBottom = useRef(true);
  useEffect(() => {
    const element = panel.current;
    if (element === null) return;
    const onScroll = () => {
      atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight <= NEAR_BOTTOM;
    };
    element.addEventListener("scroll", onScroll);
    return () => element.removeEventListener("scroll", onScroll);
  }, [panel]);
  useEffect(() => {
    const element = panel.current;
    if (element === null) return;
    if (questionStarted) atBottom.current = true;
    if (atBottom.current) element.scrollTop = element.scrollHeight;
  }, [panel, version, questionStarted]);
}
